import { AlertTriangle, Info, User } from "lucide-react";
import { Button } from "components/ui/Button";
import { formatConnectedWappPhone } from "components/PropertyManagerWhatsappCard";
import {
  WhatsAppTemplatesStatusNotice,
  type WappTemplatesStatus,
} from "components/WhatsAppTemplatesStatusNotice";
import { WhatsAppDisconnectedNotice } from "components/WhatsAppDisconnectedNotice";
import { WhatsappNumberConnectionIcon } from "components/WhatsappNumberConnectionIcon";

export type AutoResponseRule = {
  enabled?: boolean;
  weekdays?: number[];
  start?: string;
  end?: string;
};

type Tr = (es: string, en: string) => string;

const WEEKDAY_OPTIONS = [
  { value: 0, es: "Lun", en: "Mon" },
  { value: 1, es: "Mar", en: "Tue" },
  { value: 2, es: "Mié", en: "Wed" },
  { value: 3, es: "Jue", en: "Thu" },
  { value: 4, es: "Vie", en: "Fri" },
  { value: 5, es: "Sáb", en: "Sat" },
  { value: 6, es: "Dom", en: "Sun" },
];

export const DEFAULT_AUTO_RESPONSE_RULE: AutoResponseRule = {
  enabled: true,
  weekdays: [0, 1, 2, 3, 4],
  start: "09:00",
  end: "18:00",
};

function AutoResponseToggle({
  enabled,
  onChange,
  ariaLabel,
}: {
  enabled: boolean;
  onChange: (value: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={ariaLabel}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 ${
        enabled ? "bg-emerald-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`pointer-events-none absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
          enabled ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

export type CondoManagerWhatsappCardProps = {
  tr: Tr;
  locale: "es" | "en";
  /** Logged-in manager display name (card title). */
  condoManagerName: string;
  condoManagerPhone: string | null;
  managerWappPhoneNumberId: string | null;
  managerWappPhone: string | null;
  managerWappConnectLoading: boolean;
  managerWappConnectMessage: string | null;
  managerWappTokenReconnectRecommended: boolean;
  managerWappTemplatesStatus?: WappTemplatesStatus;
  managerWappDisconnected?: boolean;
  /** Company number from the Assistant tab; it must not be connected here too. */
  companyWappPhone?: string | null;
  onLaunchManagerSignup: () => void;
  managerAutoResponseEnabled: boolean;
  managerAutoResponseNextEnableAt: string | null;
  managerAutoResponseNextDisableAt: string | null;
  onManagerAutoResponseEnabledChange: (enabled: boolean) => void;
  managerAutoResponseRules: AutoResponseRule[];
  onAddAutoResponseRule: () => void;
  onRemoveAutoResponseRule: (index: number) => void;
  onUpdateAutoResponseRule: (
    index: number,
    patch: Partial<AutoResponseRule>,
  ) => void;
  onToggleRuleWeekday: (index: number, weekday: number) => void;
};

export function CondoManagerWhatsappCard({
  tr,
  locale,
  condoManagerName,
  condoManagerPhone,
  managerWappPhoneNumberId,
  managerWappPhone,
  managerWappConnectLoading,
  managerWappConnectMessage,
  managerWappTokenReconnectRecommended,
  managerWappTemplatesStatus,
  managerWappDisconnected = false,
  companyWappPhone = null,
  onLaunchManagerSignup,
  managerAutoResponseEnabled,
  managerAutoResponseNextEnableAt,
  managerAutoResponseNextDisableAt,
  onManagerAutoResponseEnabledChange,
  managerAutoResponseRules,
  onAddAutoResponseRule,
  onRemoveAutoResponseRule,
  onUpdateAutoResponseRule,
  onToggleRuleWeekday,
}: CondoManagerWhatsappCardProps) {
  const nextTransitionAt = managerAutoResponseEnabled
    ? managerAutoResponseNextDisableAt
    : managerAutoResponseNextEnableAt;
  const nextTransitionLabel = managerAutoResponseEnabled
    ? tr("Se apagará", "Will turn off")
    : tr("Se prenderá", "Will turn on");
  const formattedNextTransition = nextTransitionAt
    ? new Date(nextTransitionAt).toLocaleString(
        locale === "es" ? "es-UY" : "en-US",
        {
          dateStyle: "short",
          timeStyle: "short",
        },
      )
    : null;

  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-gray-200 bg-gray-50/50 p-3.5 sm:p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <User
            className="h-5 w-5 shrink-0 text-gray-600"
            strokeWidth={2}
            aria-hidden
          />
          <h2 className="min-w-0 text-base font-semibold tracking-tight text-gray-900">
            {locale === "es"
              ? `Respuesta automática de ${condoManagerName}`
              : `Automatic response from ${condoManagerName}`}
          </h2>
        </div>
        {managerWappPhoneNumberId ? (
          <div className="flex shrink-0 items-center gap-2 pt-0.5">
            {formattedNextTransition ? (
              <p className="text-xs leading-snug text-gray-600">
                {nextTransitionLabel}: {formattedNextTransition}
              </p>
            ) : null}
            <AutoResponseToggle
              enabled={managerAutoResponseEnabled}
              onChange={onManagerAutoResponseEnabledChange}
              ariaLabel={tr("Activar auto-response", "Enable auto-response")}
            />
          </div>
        ) : null}
      </div>
      {managerWappPhoneNumberId ? (
        <div className="space-y-1">
          <p className="text-xs leading-snug text-gray-600">
            {managerWappDisconnected
              ? tr(
                  "Tu número de WhatsApp está desconectado.",
                  "Your WhatsApp number is disconnected.",
                )
              : tr(
                  "Tu número de WhatsApp está conectado.",
                  "Your WhatsApp number is connected.",
                )}
          </p>
          <div className="flex items-center gap-2.5">
            <WhatsappNumberConnectionIcon connected={!managerWappDisconnected} />
            <p className="min-w-0 text-base font-semibold tabular-nums tracking-tight text-gray-900">
              {formatConnectedWappPhone(managerWappPhone) ||
                tr("Sincronizando…", "Syncing…")}
            </p>
          </div>
          {managerWappDisconnected ? (
            <WhatsAppDisconnectedNotice tr={tr} />
          ) : (
            <WhatsAppTemplatesStatusNotice
              tr={tr}
              status={managerWappTemplatesStatus}
              showWhenApproved
            />
          )}
        </div>
      ) : condoManagerPhone ? (
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <WhatsappNumberConnectionIcon connected={false} />
            <p className="min-w-0 text-base font-semibold tabular-nums tracking-tight text-gray-900">
              {formatConnectedWappPhone(condoManagerPhone)}
            </p>
          </div>
          <p className="text-xs leading-snug text-gray-600">
            {tr("Tu número aún no está conectado.", "Your number is not connected yet.")}
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-2.5">
          <WhatsappNumberConnectionIcon connected={false} />
          <p className="min-w-0 text-sm leading-snug text-gray-600">
            {tr("Tu número aún no está conectado.", "Your number is not connected yet.")}
          </p>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          className="shrink-0"
          onClick={onLaunchManagerSignup}
          disabled={managerWappConnectLoading}
          size="sm"
        >
          {managerWappConnectLoading
            ? tr("Conectando...", "Connecting...")
            : managerWappDisconnected
              ? tr("Reconectar WhatsApp", "Reconnect WhatsApp")
              : tr("Conectar número", "Connect number")}
        </Button>
        {!managerWappPhoneNumberId ? (
          <span className="inline-flex items-center gap-1.5 text-xs leading-snug text-gray-600">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {tr(
              "El número debe estar configurado en WhatsApp Business para poder conectarlo.",
              "The number must be configured in WhatsApp Business before connecting it.",
            )}
          </span>
        ) : null}
        {managerWappTokenReconnectRecommended ? (
          <span className="text-xs leading-snug text-amber-800">
            {tr(
              "Se recomienda reconectar WhatsApp pronto.",
              "WhatsApp reconnect is recommended soon.",
            )}
          </span>
        ) : null}
      </div>
      {companyWappPhone &&
      (!managerWappPhoneNumberId || managerWappDisconnected) ? (
        <p className="inline-flex items-start gap-1.5 text-xs leading-snug text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {tr(
            `No conectes aquí el número de la empresa (${formatConnectedWappPhone(companyWappPhone)}): lo desconectaría de Eloria. Esta respuesta automática es para tu WhatsApp personal.`,
            `Do not connect the company number (${formatConnectedWappPhone(companyWappPhone)}) here: it would disconnect it from Eloria. This automatic response is for your personal WhatsApp.`,
          )}
        </p>
      ) : null}
      {managerWappConnectMessage ? (
        <p className="text-xs leading-snug text-gray-600">
          {managerWappConnectMessage}
        </p>
      ) : null}
      {managerWappPhoneNumberId ? (
        <div className="space-y-2.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-sm font-medium text-gray-900">
              {tr("Disponibilidad", "Availability")}
            </span>
            <span className="inline-flex items-center gap-1 text-xs leading-snug text-gray-600">
              <Info className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
              {locale === "es" ? (
                <span>
                  La respuesta automática se prende <strong>fuera</strong> del
                  horario de disponibilidad.
                </span>
              ) : (
                <span>
                  Automatic replies are enabled <strong>outside</strong> your
                  availability hours.
                </span>
              )}
            </span>
          </div>
          <div className="space-y-2.5">
            {managerAutoResponseRules.map((rule, index) => (
              <div
                key={`rule-${index}`}
                className="rounded-md border border-gray-300 p-3 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={rule.enabled !== false}
                      onChange={(e) =>
                        onUpdateAutoResponseRule(index, {
                          enabled: e.target.checked,
                        })
                      }
                    />
                    {tr("Horario activo", "Availability rule enabled")}
                  </label>
                  <button
                    type="button"
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => onRemoveAutoResponseRule(index)}
                  >
                    {tr("Eliminar", "Remove")}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAY_OPTIONS.map((option) => {
                    const selected = (rule.weekdays || []).includes(
                      option.value,
                    );
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => onToggleRuleWeekday(index, option.value)}
                        className={`px-2 py-1 rounded text-xs border ${
                          selected
                            ? "bg-blue-100 border-blue-300 text-blue-700"
                            : "bg-white border-gray-300 text-gray-600"
                        }`}
                      >
                        {locale === "es" ? option.es : option.en}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600">
                      {tr("Desde", "From")}
                    </span>
                    <input
                      type="time"
                      value={rule.start || "09:00"}
                      onChange={(e) =>
                        onUpdateAutoResponseRule(index, {
                          start: e.target.value,
                        })
                      }
                      className="rounded border border-gray-300 px-2 py-1 text-xs"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600">
                      {tr("Hasta", "To")}
                    </span>
                    <input
                      type="time"
                      value={rule.end || "18:00"}
                      onChange={(e) =>
                        onUpdateAutoResponseRule(index, { end: e.target.value })
                      }
                      className="rounded border border-gray-300 px-2 py-1 text-xs"
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button
              className="shrink-0"
              onClick={onAddAutoResponseRule}
              size="sm"
            >
              {tr(
                "Agregar horario de disponibilidad",
                "Add availability hours",
              )}
            </Button>
          </div>
        </div>
      ) : null}
      {!managerWappPhoneNumberId ? (
        <div className="w-full border-t border-gray-200/80 pt-3">
          <p className="text-xs leading-snug text-gray-500">
            {tr(
              "Puedes conectar tu WhatsApp para que Eloria responda por ti cuando no estás disponible.",
              "Connect your WhatsApp so Eloria can reply for you when you’re not available.",
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}
